import { ArrowRight, Phone, ShieldCheck, Sparkles } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

export default function Hero() {
  const { settings } = useSettings();
  const phone = settings?.phone || "240-383-9887";

  return (
    <section className="relative min-h-[100vh] flex items-center pt-24 pb-16 overflow-hidden bg-[#050505]">
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1758101755915-462eddc23f57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmclMjBvbiUyMHBhbmVsfGVufDB8fHx8MTc4Mzk4NjA4NXww&ixlib=rb-4.1.0&q=85"
          alt="Electrician at work"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </div>

      {/* Grid lines */}
      <div className="absolute inset-0 z-0 opacity-[0.06]"
           style={{
             backgroundImage:
               "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
             backgroundSize: "60px 60px",
           }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8 reveal">
          <div className="inline-flex items-center gap-2 border border-yellow-400/30 bg-yellow-400/5 rounded-full px-3.5 py-1.5 mb-7">
            <Sparkles className="w-3.5 h-3.5 text-yellow-400" />
            <span className="text-xs uppercase tracking-[0.2em] text-yellow-400 font-semibold">
              Licensed • Insured • DMV Trusted
            </span>
          </div>

          <h1 className="font-display font-black text-white tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem]">
            Powering the <span className="text-yellow-400">DMV</span><br />
            with expert<br />
            electrical work.
          </h1>

          <p className="mt-7 text-white/70 text-lg lg:text-xl leading-relaxed max-w-2xl font-normal">
            {settings?.tagline ||
              "From EV chargers to full home rewiring — B&J Electric Service LLC delivers safe, code-compliant work across DC, Maryland & Virginia."}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#quote"
              data-testid="hero-quote-button"
              className="group inline-flex items-center gap-2.5 bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-7 py-4 rounded-md text-base transition-all hover:scale-[1.03] active:scale-95 shadow-[0_0_35px_rgba(250,204,21,0.25)]"
            >
              Get a Free Quote
              <ArrowRight className="w-4.5 h-4.5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={`tel:${phone.replace(/[^0-9]/g, "")}`}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2.5 border border-white/20 hover:border-yellow-400/60 hover:bg-white/5 text-white font-medium px-7 py-4 rounded-md text-base transition-all"
            >
              <Phone className="w-4 h-4" />
              Call {phone}
            </a>
          </div>

          <div className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-5">
            {[
              { label: "Years in the DMV", value: "15+" },
              { label: "Homes wired", value: "2,400+" },
              { label: "Response time", value: "< 24h" },
            ].map((s) => (
              <div key={s.label} className="border-l-2 border-yellow-400/60 pl-4">
                <div className="text-white font-display font-black text-3xl leading-none tracking-tight">{s.value}</div>
                <div className="text-white/50 text-xs uppercase tracking-[0.18em] mt-1.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right floating card */}
        <div className="lg:col-span-4 hidden lg:block reveal" style={{ animationDelay: "0.3s" }}>
          <div className="relative">
            <div className="absolute -inset-2 bg-yellow-400/10 blur-2xl rounded-2xl" />
            <div className="relative bg-[#0f0f11] border border-white/10 rounded-2xl p-7">
              <ShieldCheck className="w-10 h-10 text-yellow-400 mb-4" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.22em] text-white/50 mb-2">24/7 Emergency</div>
              <h3 className="font-display text-2xl text-white font-semibold mb-3 tracking-tight">
                Power's out?<br />We answer fast.
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                Emergency electrical situations don't wait. Neither do we. Call anytime.
              </p>
              <a
                href={`tel:${phone.replace(/[^0-9]/g, "")}`}
                data-testid="hero-emergency-call"
                className="w-full inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-yellow-400 hover:text-black text-white font-medium py-3 rounded-md transition-all border border-white/10"
              >
                <Phone className="w-4 h-4" />
                {phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</div>
        <div className="w-px h-10 bg-gradient-to-b from-yellow-400 to-transparent" />
      </div>
    </section>
  );
}
